import * as THREE from 'three';
import { LANE_WIDTH } from '../game/tuning';
import { mergeBoxes } from '../utils/geometry';
import { PALETTE } from './palette';

export type ObstacleFamily = 'firewall' | 'pylon' | 'drone' | 'gate';

export interface Footprint {
  halfWidth: number;
  halfLength: number;
}

export interface ObstaclePair {
  /** Dark authored silhouette. */
  body: THREE.InstancedMesh;
  /** Bright emissive telegraph parts sharing the body transform. */
  signal: THREE.InstancedMesh;
}

export interface ObstacleAssets {
  meshes: Map<ObstacleFamily, ObstaclePair>;
  footprints: Map<ObstacleFamily, Footprint>;
  dispose(): void;
}

/**
 * Four obstacle families, each split into a dark body silhouette plus an
 * emissive signal layer so the danger color reads as authored lighting on a
 * machine instead of a uniformly glowing block. Two draw calls per family.
 */
export function buildObstacleAssets(maxPerFamily: number): ObstacleAssets {
  const meshes = new Map<ObstacleFamily, ObstaclePair>();
  const footprints = new Map<ObstacleFamily, Footprint>();
  const disposables: Array<{ dispose(): void }> = [];

  const make = (
    family: ObstacleFamily,
    bodyGeometry: THREE.BufferGeometry,
    signalGeometry: THREE.BufferGeometry,
    color: string,
    footprint: Footprint,
  ): void => {
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: '#10101c',
      roughness: 0.38,
      metalness: 0.72,
      emissive: color,
      emissiveIntensity: 0.12,
    });
    const signalMaterial = new THREE.MeshStandardMaterial({
      color: '#0a0a12',
      emissive: color,
      emissiveIntensity: 2.1,
      roughness: 0.35,
      metalness: 0.1,
    });
    const body = new THREE.InstancedMesh(bodyGeometry, bodyMaterial, maxPerFamily);
    const signal = new THREE.InstancedMesh(signalGeometry, signalMaterial, maxPerFamily);
    for (const mesh of [body, signal]) {
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.count = 0;
      mesh.frustumCulled = false; // instances span the whole track; skip per-frame bounds
    }
    body.castShadow = true;
    meshes.set(family, { body, signal });
    footprints.set(family, footprint);
    disposables.push(bodyGeometry, signalGeometry, bodyMaterial, signalMaterial);
  };

  const W = LANE_WIDTH;

  // Firewall: low energy barrier — end posts and rails hold three hot bars.
  make(
    'firewall',
    mergeBoxes([
      { geo: new THREE.BoxGeometry(0.2, 1.16, 0.3), pos: [-W * 0.43, 0.58, 0] },
      { geo: new THREE.BoxGeometry(0.2, 1.16, 0.3), pos: [W * 0.43, 0.58, 0] },
      { geo: new THREE.BoxGeometry(W * 0.92, 0.14, 0.3), pos: [0, 0.08, 0] },
      { geo: new THREE.BoxGeometry(W * 0.92, 0.1, 0.24), pos: [0, 1.1, 0] },
    ]),
    mergeBoxes([
      { geo: new THREE.BoxGeometry(0.09, 0.82, 0.07), pos: [-W * 0.22, 0.6, 0] },
      { geo: new THREE.BoxGeometry(0.09, 0.82, 0.07), pos: [0, 0.6, 0] },
      { geo: new THREE.BoxGeometry(0.09, 0.82, 0.07), pos: [W * 0.22, 0.6, 0] },
      { geo: new THREE.BoxGeometry(W * 0.86, 0.06, 0.1), pos: [0, 1.04, 0] },
    ]),
    PALETTE.hazardFirewall,
    { halfWidth: W * 0.45, halfLength: 0.22 },
  );

  // Pylon: tapered relay column with glowing stack rings and a beacon tip.
  make(
    'pylon',
    mergeBoxes([
      { geo: new THREE.BoxGeometry(1.0, 0.34, 1.0), pos: [0, 0.17, 0] },
      { geo: new THREE.BoxGeometry(0.62, 3.1, 0.62), pos: [0, 1.9, 0] },
      { geo: new THREE.BoxGeometry(0.82, 0.2, 0.82), pos: [0, 2.6, 0] },
      { geo: new THREE.BoxGeometry(0.46, 0.7, 0.46), pos: [0, 3.75, 0] },
      { geo: new THREE.BoxGeometry(0.08, 0.7, 0.08), pos: [0, 4.35, 0] },
    ]),
    mergeBoxes([
      { geo: new THREE.BoxGeometry(0.7, 0.08, 0.7), pos: [0, 1.15, 0] },
      { geo: new THREE.BoxGeometry(0.7, 0.08, 0.7), pos: [0, 2.05, 0] },
      { geo: new THREE.BoxGeometry(0.54, 0.08, 0.54), pos: [0, 3.35, 0] },
      { geo: new THREE.BoxGeometry(0.14, 0.14, 0.14), pos: [0, 4.55, 0] },
    ]),
    PALETTE.hazardPylon,
    { halfWidth: 0.48, halfLength: 0.48 },
  );

  // Drone: X-frame quad with rotor pods and a forward sensor eye.
  make(
    'drone',
    mergeBoxes([
      { geo: new THREE.BoxGeometry(0.58, 0.3, 0.72), pos: [0, 0, 0] },
      { geo: new THREE.BoxGeometry(1.5, 0.07, 0.16), pos: [0, 0.1, 0], rot: [0, 0.6, 0] },
      { geo: new THREE.BoxGeometry(1.5, 0.07, 0.16), pos: [0, 0.1, 0], rot: [0, -0.6, 0] },
      { geo: new THREE.BoxGeometry(0.22, 0.34, 0.22), pos: [0, 0.28, 0.1] },
    ]),
    mergeBoxes([
      { geo: new THREE.BoxGeometry(0.2, 0.1, 0.2), pos: [0.62, 0.16, 0.42], rot: [0, 0.6, 0] },
      { geo: new THREE.BoxGeometry(0.2, 0.1, 0.2), pos: [-0.62, 0.16, 0.42], rot: [0, -0.6, 0] },
      { geo: new THREE.BoxGeometry(0.2, 0.1, 0.2), pos: [0.62, 0.16, -0.42], rot: [0, -0.6, 0] },
      { geo: new THREE.BoxGeometry(0.2, 0.1, 0.2), pos: [-0.62, 0.16, -0.42], rot: [0, 0.6, 0] },
      { geo: new THREE.BoxGeometry(0.34, 0.09, 0.06), pos: [0, 0.02, -0.38] },
    ]),
    PALETTE.hazardDrone,
    { halfWidth: 0.62, halfLength: 0.5 },
  );

  // Gate blocker: shutter frame with slats and chevron warning strips.
  make(
    'gate',
    mergeBoxes([
      { geo: new THREE.BoxGeometry(0.24, 2.8, 0.3), pos: [-W * 0.44, 1.4, 0] },
      { geo: new THREE.BoxGeometry(0.24, 2.8, 0.3), pos: [W * 0.44, 1.4, 0] },
      { geo: new THREE.BoxGeometry(W * 0.96, 0.24, 0.42), pos: [0, 2.72, 0] },
      { geo: new THREE.BoxGeometry(W * 0.8, 0.5, 0.18), pos: [0, 0.45, 0] },
      { geo: new THREE.BoxGeometry(W * 0.8, 0.5, 0.18), pos: [0, 1.25, 0] },
      { geo: new THREE.BoxGeometry(W * 0.8, 0.5, 0.18), pos: [0, 2.05, 0] },
    ]),
    mergeBoxes([
      { geo: new THREE.BoxGeometry(W * 0.62, 0.09, 0.1), pos: [0, 0.85, 0], rot: [0, 0, 0.32] },
      { geo: new THREE.BoxGeometry(W * 0.62, 0.09, 0.1), pos: [0, 0.85, 0], rot: [0, 0, -0.32] },
      { geo: new THREE.BoxGeometry(W * 0.62, 0.09, 0.1), pos: [0, 1.65, 0], rot: [0, 0, 0.32] },
      { geo: new THREE.BoxGeometry(W * 0.62, 0.09, 0.1), pos: [0, 1.65, 0], rot: [0, 0, -0.32] },
      { geo: new THREE.BoxGeometry(W * 0.9, 0.07, 0.12), pos: [0, 2.6, 0] },
    ]),
    PALETTE.hazardGate,
    { halfWidth: W * 0.47, halfLength: 0.28 },
  );

  return {
    meshes,
    footprints,
    dispose() {
      for (const item of disposables) item.dispose();
      for (const pair of meshes.values()) {
        pair.body.dispose();
        pair.signal.dispose();
      }
    },
  };
}
